import React, { createContext, useContext, useState, ReactNode } from "react";

export interface MenuItem {
  id: number;
  name: string;
  img: string | File;
  price: number;
  discountRate?: number; // 할인율 추가
  quantity?: number;
}

export interface Pack {
  items: MenuItem[];
  isSampleMenu?: boolean;
  closingHours?: string;
  quantity?: number;
}

interface PackContextProps {
  packs: Pack[];
  addPack: (
    items: MenuItem[],
    isSampleMenu?: boolean,
    closingHours?: string,
    quantity?: number
  ) => void;
  resetPacks: () => void;
  deletePack: (index: number) => void; // deletePack 추가
  updatePackQuantity: (index: number, quantity: number) => void; // updatePackQuantity 추가
  updateItemQuantity: (
    packIndex: number,
    itemId: number,
    quantity: number
  ) => void; // updateItemQuantity 추가
}

const PackContext = createContext<PackContextProps | undefined>(undefined);

export const usePackContext = () => {
  const context = useContext(PackContext);
  if (!context) {
    throw new Error("usePackContext must be used within a PackProvider");
  }
  return context;
};

export const PackProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [packs, setPacks] = useState<Pack[]>([]);

  const addPack = (
    items: MenuItem[],
    isSampleMenu?: boolean,
    closingHours?: string,
    quantity?: number
  ) => {
    setPacks((prevPacks) => [
      ...prevPacks,
      { items, isSampleMenu, closingHours, quantity },
    ]);
  };

  const resetPacks = () => {
    setPacks([]); // reset the packs to an empty array
  };

  const deletePack = (index: number) => {
    setPacks((prevPacks) => prevPacks.filter((_, i) => i !== index));
  };

  const updatePackQuantity = (index: number, quantity: number) => {
    setPacks((prevPacks) => {
      const updatedPacks = [...prevPacks];
      if (updatedPacks[index]) {
        updatedPacks[index].quantity = quantity; // 수량 업데이트
      }
      return updatedPacks;
    });
  };

  const updateItemQuantity = (
    packIndex: number,
    itemId: number,
    quantity: number
  ) => {
    setPacks((prevPacks) => {
      // 기존 팩 배열을 복사하여 불변성 유지
      const updatedPacks = [...prevPacks];

      // 지정된 인덱스의 팩이 존재하는지 확인
      if (updatedPacks[packIndex]) {
        // 해당 팩의 아이템 배열을 순회하며 특정 아이템의 수량만 업데이트
        updatedPacks[packIndex].items = updatedPacks[packIndex].items.map(
          (item) =>
            // 아이템 ID가 일치하는 경우에만 수량을 새로운 값으로 업데이트
            // 일치하지 않는 경우 기존 아이템을 그대로 유지
            item.id === itemId ? { ...item, quantity: quantity } : item
        );
      }

      // 업데이트된 팩 배열을 반환하여 상태 업데이트
      return updatedPacks;
    });
  };

  return (
    <PackContext.Provider
      value={{
        packs,
        addPack,
        resetPacks,
        deletePack,
        updatePackQuantity,
        updateItemQuantity,
      }}
    >
      {children}
    </PackContext.Provider>
  );
};
